import { useParams, useNavigate, Link } from "react-router-dom";
import { projectCategories } from "../../mocks/projects";
import { useScrollReveal, useDrawLine } from "../../hooks/useScrollReveal";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import ProjectCard from "./components/ProjectCard";

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const cat = projectCategories.find((c) => c.slug === category);

  const heroLabel = useScrollReveal<HTMLDivElement>({ delay: 80, direction: "up", distance: 30, duration: 800 });
  const sectionLine = useDrawLine<HTMLDivElement>({ delay: 200, duration: 900 });

  if (!cat) {
    return (
      <div className="min-h-screen bg-off-white flex flex-col items-center justify-center px-6">
        <h1 className="font-serif text-4xl text-charcoal mb-4">
          Category not found
        </h1>
        <Link
          to="/"
          className="font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const handleNavigate = (id: string) => {
    if (id === "home") {
      navigate("/");
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-off-white">
      <Navbar onNavigate={handleNavigate} />

      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-sans text-sm text-warm-gray hover:text-teal transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </button>

          <div className="relative h-[500px] md:h-[650px] rounded-lg overflow-hidden mb-10 md:mb-14">
            <img
              src={cat.thumbnail}
              alt={cat.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-tr from-teal/10 via-transparent to-transparent" />
            <div ref={heroLabel} className="absolute bottom-0 left-0 p-8 md:p-12">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-2">
                {cat.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-6 h-px bg-teal/70" />
                <p className="font-sans text-sm text-white/80 tracking-wide">
                  {cat.count} Projects
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-8 md:mb-10 overflow-hidden">
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal whitespace-nowrap">
              Projects
            </h2>
            <div className="flex-1">
              <div
                ref={sectionLine}
                style={{ width: "0%" }}
                className="h-[2px] bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
              />
            </div>
            <span className="font-sans text-xs text-warm-gray tracking-wider hidden sm:block">
              {cat.count} total
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {cat.projects.map((project, idx) => (
              <ProjectCard
                key={project.id}
                project={project}
                categorySlug={cat.slug}
                index={idx}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}